import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style as StatusBarStyle } from '@capacitor/status-bar';
import { ViewportService } from '@ui';
import { SystemUi } from '../plugins/system-ui.plugin';

const WINDOW_BACKGROUND_CSS_VARIABLE = '--color-background-mobile-header';

function toHexColor(value: string): string | null {
  const trimmed = value.trim();

  if (/^#[0-9a-f]{6}$/i.test(trimmed)) {
    return trimmed;
  }

  const rgb = trimmed.match(/^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (!rgb) {
    return null;
  }

  return (
    '#' +
    [rgb[1], rgb[2], rgb[3]]
      .map((part) => Number(part).toString(16).padStart(2, '0'))
      .join('')
  );
}

@Injectable({
  providedIn: 'root',
})
export class MobileStatusBarService {
  private readonly viewportService: ViewportService = inject(ViewportService);

  public async hide(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await StatusBar.hide();
    } catch {}
  }

  public async show(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await StatusBar.show();
    } catch {}
  }

  public async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch {}
  }

  public async setTheme(theme: 'light' | 'dark'): Promise<void> {
    const style = theme === 'dark' ? StatusBarStyle.Light : StatusBarStyle.Dark;

    try {
      await StatusBar.setStyle({ style });
      await StatusBar.show();
    } catch {}

    await this.syncWindowBackground();
  }

  private async syncWindowBackground(): Promise<void> {
    if (!this.viewportService.isNativeAndroid()) {
      return;
    }

    const raw = getComputedStyle(document.body).getPropertyValue(
      WINDOW_BACKGROUND_CSS_VARIABLE,
    );
    const color = toHexColor(raw);

    if (!color) {
      return;
    }

    try {
      await SystemUi.setWindowBackground({ color });
    } catch {}
  }
}
